import * as THREE from "three";

/**
 * ItemIcons renders each item's actual 3D model to a small thumbnail (a data
 * URL), cached by model key, so inventory slots show the real weapon / potion /
 * resource instead of an emoji. A tiny offscreen renderer does the work once
 * per unique item; the HUD just uses the returned image.
 */
export class ItemIcons {
  constructor(registry) {
    this.registry = registry;
    this.cache = {};
    this.size = 96;
    this.renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, preserveDrawingBuffer: true });
    this.renderer.setSize(this.size, this.size);
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this.scene = new THREE.Scene();
    this.scene.add(new THREE.AmbientLight(0xffffff, 0.9));
    const key = new THREE.DirectionalLight(0xffffff, 1.5); key.position.set(2, 4, 3); this.scene.add(key);
    const rim = new THREE.DirectionalLight(0x88aaff, 0.5); rim.position.set(-3, 1, -2); this.scene.add(rim);
    this.camera = new THREE.PerspectiveCamera(35, 1, 0.01, 100);
  }

  _placeholder(color) {
    const c = color ? new THREE.Color(color) : new THREE.Color(0x888888);
    return new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), new THREE.MeshStandardMaterial({ color: c, roughness: 0.6 }));
  }

  /** Pre-render icons for a list of model keys so gameplay never stalls on a render. */
  prewarm(keys) {
    for (const k of keys) this.iconFor({ model: k, kind: "prewarm" });
    this.iconFor({ kind: "armor" });
  }

  /**
   * Portrait of a hero posed in an idle stance with their weapon in hand
   * (used by the character-select cards). Cached per model+weapon.
   */
  renderCharacter(modelKey, weaponKey) {
    const cacheKey = `char:${modelKey}:${weaponKey || ""}`;
    if (this.cache[cacheKey]) return this.cache[cacheKey];
    if (!modelKey || !this.registry.has(modelKey)) return null;

    const model = this.registry.instance(modelKey);

    // pose into an idle stance using the shared KayKit Rig_Medium clips
    let clips = model.animations ? model.animations.slice() : [];
    clips = clips.concat(this.registry.clips("anim/kaykit_general"), this.registry.clips("anim/kaykit_movement"));
    const idle = clips.find((c) => /idle|breath|stand/i.test(c.name)) || clips[0];
    let mixer = null;
    if (idle) { try { mixer = new THREE.AnimationMixer(model); mixer.clipAction(idle).play(); mixer.update(0.7); } catch (_) {} }

    // weapon in the right hand
    if (weaponKey && this.registry.has(weaponKey)) {
      const hand = model.getObjectByName("handslotr") || model.getObjectByName("handr");
      if (hand) hand.add(this.registry.instance(weaponKey));
    }

    const holder = new THREE.Group();
    holder.add(model);
    model.updateWorldMatrix(true, true);
    const box = new THREE.Box3().setFromObject(model);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    model.position.sub(center);
    holder.rotation.set(0.12, 0.45, 0);   // slight tilt + turn — heroic front 3/4
    this.scene.add(holder);

    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    this.camera.position.set(0, 0, maxDim * 1.7);
    this.camera.lookAt(0, 0, 0);

    let url = null;
    try { this.renderer.render(this.scene, this.camera); url = this.renderer.domElement.toDataURL("image/png"); } catch (_) { url = null; }
    this.scene.remove(holder);
    if (mixer) mixer.stopAllAction();

    this.cache[cacheKey] = url;
    return url;
  }

  /** Data URL thumbnail for an item, or null if it can't be built. */
  iconFor(item) {
    if (!item) return null;
    const cacheKey = item.model || (item.kind === "armor" ? "k:armor" : `k:${item.kind}:${item.color || ""}`);
    if (this.cache[cacheKey]) return this.cache[cacheKey];

    let obj = null;
    if (item.model && this.registry.has(item.model)) obj = this.registry.instance(item.model);
    else if (item.kind === "armor" && this.registry.has("weapon/shield")) obj = this.registry.instance("weapon/shield");
    if (!obj) obj = this._placeholder(item.color);

    const holder = new THREE.Group();
    holder.add(obj);
    obj.updateWorldMatrix(true, true);
    const box = new THREE.Box3().setFromObject(obj);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    obj.position.sub(center);                    // center at origin
    holder.rotation.set(0.5, 0.7, 0);            // 3/4 hero-shot angle
    this.scene.add(holder);

    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    this.camera.position.set(0, 0, maxDim * 1.9);
    this.camera.lookAt(0, 0, 0);

    let url = null;
    try {
      this.renderer.render(this.scene, this.camera);
      url = this.renderer.domElement.toDataURL("image/png");
    } catch (_) { url = null; }
    this.scene.remove(holder);

    this.cache[cacheKey] = url;
    return url;
  }
}
